import React, {useRef, useState} from 'react';
import {
    Box,
    Button,
    Grid,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import Api from '../../../../store/Api';
import {DuplicateCheckLimits, LeadResult, MappedLead} from '../../../../store/models/DuplicateCheck';
import {formatDuration, makeLeadResult, placeToLead} from '../../../../util/DuplicateCheckUtil';
import {INTER_REQUEST_DELAY_MS, MAX_RATE_LIMIT_RETRIES, RATE_LIMIT_BACKOFF_MS} from './constants';
import {StatusBadge} from './shared';
import WeightsPanel from './WeightsPanel';

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const useStyles = makeStyles((theme) => ({
    errorText: {
        color: theme.palette.error.main,
    },
    warning: {
        color: theme.palette.warning.main,
    },
    flexRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
    },
    summaryCaption: {
        marginBottom: theme.spacing(1),
    },
    tableContainer: {
        marginTop: theme.spacing(2),
        maxHeight: 560,
        overflow: 'auto',
    },
    blockCaption: {
        display: 'block',
    },
}));

interface BatchRunPanelProps {
    topN?: number;
    onFormChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    maxTopN: number;
    weightsOpen: boolean;
    onWeightsToggle: () => void;
    weightValues: Record<string, number>;
    onWeightChange: (key: string, raw: string) => void;
    onWeightsReset: () => void;
    weightsPayload: Record<string, number>;
    limits: DuplicateCheckLimits | null;
    onLimits: (limits: DuplicateCheckLimits) => void;
}

export default function BatchRunPanel(props: BatchRunPanelProps) {
    const classes = useStyles();
    const {
        topN, onFormChange, maxTopN, weightsOpen, onWeightsToggle, weightValues,
        onWeightChange, onWeightsReset, weightsPayload, limits, onLimits,
    } = props;

    const [jsonInput, setJsonInput] = useState('');
    const [runResults, setRunResults] = useState<LeadResult[]>([]);
    const [running, setRunning] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [avgMs, setAvgMs] = useState<number | null>(null);
    const [rateLimitWait, setRateLimitWait] = useState(false);
    const [batchError, setBatchError] = useState<string | null>(null);
    const [cancelled, setCancelled] = useState(false);
    const cancelRef = useRef(false);

    const handleRun = async () => {
        let leads: MappedLead[];
        try {
            const parsed = JSON.parse(jsonInput);
            const places = Array.isArray(parsed) ? parsed : parsed?.places;
            if (!Array.isArray(places)) {
                throw new Error('JSON muss ein Array oder ein Objekt mit "places"-Array sein');
            }
            leads = places.map(placeToLead);
        } catch (err: any) {
            setBatchError(err instanceof SyntaxError ? 'Ungültiges JSON' : (err?.message ?? 'Fehler beim Parsen'));
            return;
        }

        if (leads.length === 0) {
            setBatchError('Keine Einträge im JSON gefunden.');
            return;
        }

        cancelRef.current = false;
        setCancelled(false);
        setBatchError(null);
        setRunResults([]);
        setCurrentIndex(0);
        setTotalCount(leads.length);
        setAvgMs(null);
        setRunning(true);

        const results: LeadResult[] = [];
        const durations: number[] = [];

        try {
            for (let i = 0; i < leads.length; i++) {
                if (cancelRef.current) break;
                setCurrentIndex(i);
                const lead = leads[i];

                let rateLimitRetries = 0;
                let pending = true;
                while (pending && !cancelRef.current) {
                    const started = performance.now();
                    try {
                        const res = await Api.duplicateCheckDetailedWithStatus({
                            ...lead.params,
                            topN,
                            weights: weightsPayload,
                        });
                        if (res.ok) {
                            durations.push(performance.now() - started);
                            setAvgMs(durations.reduce((a, b) => a + b, 0) / durations.length);
                            if (res.data?.config?.limits) onLimits(res.data.config.limits);
                            results.push(makeLeadResult(lead, res.data));
                            pending = false;
                        } else if (res.status === 429 && rateLimitRetries < MAX_RATE_LIMIT_RETRIES) {
                            rateLimitRetries++;
                            setRateLimitWait(true);
                            await sleep(RATE_LIMIT_BACKOFF_MS);
                            setRateLimitWait(false);
                        } else {
                            results.push({lead, status: 'error', bestScore: null, error: `${res.status}: ${res.message}`});
                            pending = false;
                        }
                    } catch (err: any) {
                        results.push({
                            lead,
                            status: 'error',
                            bestScore: null,
                            error: typeof err === 'string' ? err : (err?.message ?? 'Netzwerkfehler'),
                        });
                        pending = false;
                    }
                }

                setRunResults([...results]);
                if (!cancelRef.current && i < leads.length - 1) {
                    await sleep(INTER_REQUEST_DELAY_MS);
                }
            }
        } finally {
            setRunning(false);
            setRateLimitWait(false);
        }
    };

    const handleCancel = () => {
        cancelRef.current = true;
        setCancelled(true);
    };

    const counts = runResults.reduce(
        (acc, r) => {
            acc[r.status] += 1;
            return acc;
        },
        {match: 0, uncertain: 0, new: 0, error: 0},
    );

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                    Google Places JSON einfügen — entweder das places-Array direkt
                    oder ein Objekt mit <code>"places": [...]</code>. Jeder Eintrag wird
                    nacheinander einzeln geprüft; die Gewichtung unten gilt für den ganzen Lauf.
                </Typography>
            </Grid>
            <Grid item xs={6} md={2}>
                <TextField fullWidth name="topN" label="Top-N Kandidaten"
                           type="number" inputProps={{min: 1, max: maxTopN, step: 1}}
                           helperText={`max. ${maxTopN}`}
                           value={topN ?? ''} onChange={onFormChange}
                           disabled={running}/>
            </Grid>
            <Grid item xs={12}>
                <WeightsPanel
                    open={weightsOpen}
                    onToggle={onWeightsToggle}
                    values={weightValues}
                    onChange={onWeightChange}
                    onReset={onWeightsReset}
                    disabled={running}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    multiline
                    minRows={6}
                    maxRows={16}
                    variant="outlined"
                    placeholder={'[\n  { "id": "ChIJ...", "displayName": { "text": "..." }, ... }\n]'}
                    value={jsonInput}
                    onChange={(e) => setJsonInput(e.target.value)}
                    disabled={running}
                />
            </Grid>
            <Grid item xs={12}>
                <Box className={classes.flexRow}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleRun}
                        disabled={running || !jsonInput.trim()}
                    >
                        {running ? 'Läuft…' : 'Alle prüfen'}
                    </Button>
                    {running && (
                        <Button variant="outlined" onClick={handleCancel}>
                            Abbrechen
                        </Button>
                    )}
                    <Typography variant="caption" color="textSecondary">
                        {limits?.rateLimit
                            ? `Rate-Limit: max. ${limits.rateLimit.maxRequests} Anfragen / ${limits.rateLimit.ttlSeconds}s — bei Erreichen pausiert der Lauf automatisch.`
                            : 'Bei Erreichen des Server-Rate-Limits pausiert der Lauf automatisch kurz.'}
                    </Typography>
                </Box>
            </Grid>

            {(running || runResults.length > 0) && totalCount > 0 && (
                <Grid item xs={12}>
                    <LinearProgress
                        variant="determinate"
                        value={Math.round((runResults.length / totalCount) * 100)}
                    />
                    <Box mt={1} className={classes.flexRow}>
                        <Typography variant="body2">
                            {running
                                ? `Eintrag ${Math.min(currentIndex + 1, totalCount)} von ${totalCount}`
                                : `${cancelled ? 'Abgebrochen' : 'Fertig'} — ${runResults.length} von ${totalCount} verarbeitet`}
                        </Typography>
                        {running && avgMs != null && runResults.length < totalCount && (
                            <Typography variant="body2" color="textSecondary">
                                ca. {formatDuration(avgMs * (totalCount - runResults.length))} verbleibend
                            </Typography>
                        )}
                        {rateLimitWait && (
                            <Typography variant="body2" className={classes.warning}>
                                Rate-Limit erreicht – kurze Pause…
                            </Typography>
                        )}
                    </Box>
                </Grid>
            )}

            {batchError && (
                <Grid item xs={12}>
                    <Typography className={classes.errorText}>{batchError}</Typography>
                </Grid>
            )}

            {runResults.length > 0 && (
                <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary" className={classes.summaryCaption}>
                        {runResults.length} Ergebnisse — {counts.match} Treffer, {counts.uncertain} unsicher, {counts.new} neu
                        {counts.error > 0 ? `, ${counts.error} Fehler` : ''}
                    </Typography>
                    <TableContainer className={classes.tableContainer}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Eintrag</TableCell>
                                    <TableCell>Place-ID</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Score</TableCell>
                                    <TableCell>Bester Kandidat</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {runResults.map((r, idx) => (
                                    <TableRow key={r.lead.placeId || idx}>
                                        <TableCell>{r.lead.display}</TableCell>
                                        <TableCell>
                                            <Typography variant="caption">{r.lead.placeId || '—'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <StatusBadge status={r.status}/>
                                            {r.exactPlacesMatch && (
                                                <Typography variant="caption" color="textSecondary" className={classes.blockCaption}>
                                                    exakter Places-Match
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {r.bestScore != null ? `${Math.round(r.bestScore * 100)} %` : '—'}
                                        </TableCell>
                                        <TableCell>
                                            {r.status === 'error' ? (
                                                <Typography variant="caption" className={classes.errorText}>
                                                    {r.error}
                                                </Typography>
                                            ) : r.bestCandidateName ? (
                                                <>
                                                    {r.bestCandidateName}
                                                    {r.bestCandidateSap && (
                                                        <Typography component="span" variant="caption" color="textSecondary">
                                                            {' '}(SAP-Nr. {r.bestCandidateSap})
                                                        </Typography>
                                                    )}
                                                </>
                                            ) : (
                                                <Typography variant="caption" color="textSecondary">—</Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            )}
        </Grid>
    );
}
